import { cn, toast } from "@devhop/ui";
import { Cloud, CloudOff, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useOpenShift } from "../api/queries";
import { usePOSContext } from "../context/POSContext";
import {
  getAutoSyncStatus,
  runAutoSyncNow,
  subscribeAutoSyncStatus,
  type AutoSyncScope,
} from "../lib/offline-sync/auto-sync";
import { useOfflineSyncSummary } from "../lib/offline-sync/use-offline-sync-summary";

export function OfflineSyncStatusBadge({
  className,
  compact = false,
}: {
  className?: string;
  compact?: boolean;
}) {
  const { selection } = usePOSContext();
  const summary = useOfflineSyncSummary();
  const { data: openShift } = useOpenShift(
    selection?.branchId ?? null,
    selection?.deviceId ?? null,
  );
  const [online, setOnline] = useState(() => navigator.onLine);
  const [autoStatus, setAutoStatus] = useState(getAutoSyncStatus);

  useEffect(() => subscribeAutoSyncStatus(setAutoStatus), []);

  useEffect(() => {
    const updateOnlineStatus = () => setOnline(navigator.onLine);
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);
    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  const scope: AutoSyncScope | null =
    selection?.branchId &&
    selection.deviceId &&
    openShift?.id &&
    openShift.status === "OPEN"
      ? {
          branchId: selection.branchId,
          deviceId: selection.deviceId,
          shiftId: openShift.id,
        }
      : null;

  const isSyncing = autoStatus.running;
  const isPending =
    summary.pending > 0 || autoStatus.pendingReason != null;
  const showBadge = !online || isPending || isSyncing;

  const handleSyncNow = useCallback(async () => {
    if (!scope) {
      toast.error("ບໍ່ພົບກະເປີດ — ບໍ່ສາມາດ sync ໄດ້");
      return;
    }
    if (!online) {
      toast.error("ຕ້ອງ online ກ່ອນ sync");
      return;
    }
    if (isSyncing) return;

    try {
      const result = await runAutoSyncNow("manual", scope);
      if (result && result.synced > 0) {
        toast.success(`sync ສຳເລັດ ${result.synced} ລາຍການ`);
        return;
      }
      if (result && result.failed > 0) {
        toast.warning(
          `sync ບາງລາຍການລົ້ມເຫຼວ (${result.failed}) — ກວດໃນ Offline sync`,
        );
        return;
      }
      toast.info("ບໍ່ມີລາຍການຄ້າງ sync");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "sync ລົ້ມເຫຼວ",
      );
    }
  }, [scope, online, isSyncing]);

  if (!showBadge) {
    return null;
  }

  const canSync = Boolean(scope) && online && !isSyncing;

  return (
    <button
      type="button"
      disabled={!canSync}
      onClick={() => void handleSyncNow()}
      className={cn(
        compact
          ? "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs"
          : "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs",
        online
          ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100/80 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-950/60"
          : "border-destructive/30 bg-destructive/10 text-destructive",
        canSync && "cursor-pointer",
        !canSync && "cursor-default opacity-80",
        className,
      )}
      title={
        !online
          ? "POS offline"
          : isSyncing
            ? "ກຳລັງ sync..."
            : summary.pending > 0
              ? `${summary.pending} ລາຍການຄ້າງ sync — ກົດເພື່ອ sync ທັນທີ`
              : "ກົດເພື່ອ sync ທັນທີ"
      }
    >
      {isSyncing ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : online ? (
        <Cloud className="h-3.5 w-3.5" />
      ) : (
        <CloudOff className="h-3.5 w-3.5" />
      )}
      <span>
        {isSyncing ? "ກຳລັງ sync" : online ? "ລໍຖ້າ sync" : "offline"}
      </span>
      {summary.pending > 0 && (
        <span className="font-semibold">{summary.pending}</span>
      )}
    </button>
  );
}
